import { motion } from "@smartsheet/design-tokens";
import { BehaviorSubject } from "rxjs";

// From https://sheribyrnehaber.medium.com/designing-toast-messages-for-accessibility-fb610ac364be
//
// A good length of time to keep messages up is 5 seconds plus 1 extra second for every 120 words,
// rounding up. This is how fast the average American reads. That means the shortest default that
// should be used as a best practice is 6 seconds.
//
// We'll go with 7 to be safe.
export const AUTO_CLOSE_DELAY_MS = 7000;
export class SdsToastManager {
  toastStack$ = new BehaviorSubject([]);
  timerMap = new Map();
  constructor() {}
  createSimpleToast = _ref => {
    let {
      message,
      type,
      autoCloseDelay = AUTO_CLOSE_DELAY_MS
    } = _ref;
    const id = this.getNewInternalId();
    this.toastStack$.next([{
      id,
      status: "entering",
      message,
      type: type,
      entryType: "simple"
    }, ...this.toastStack$.getValue()]);

    // update the status to entered.
    this.startTimeout(id, motion.timing.default, () => {
      this.toastStack$.next(this.toastStack$.getValue().map(toast => {
        if (toast.id === id) {
          return {
            ...toast,
            status: "entered"
          };
        } else {
          return toast;
        }
      }));
      this.queueForRemoval(id, autoCloseDelay);
    });
  };
  createSimpleToastWithTheme = _ref2 => {
    let {
      message,
      type,
      themeKey,
      autoCloseDelay = AUTO_CLOSE_DELAY_MS
    } = _ref2;
    const id = this.getNewInternalId();
    this.toastStack$.next([{
      id,
      status: "entering",
      message,
      type: type,
      entryType: "simple",
      themeKey
    }, ...this.toastStack$.getValue()]);

    // update the status to entered.
    this.startTimeout(id, motion.timing.default, () => {
      this.toastStack$.next(this.toastStack$.getValue().map(toast => {
        if (toast.id === id) {
          return {
            ...toast,
            status: "entered"
          };
        } else {
          return toast;
        }
      }));
      this.queueForRemoval(id, autoCloseDelay);
    });
  };
  requestInteractiveToast = _ref3 => {
    let {
      onElementReady,
      autoCloseDelay = AUTO_CLOSE_DELAY_MS
    } = _ref3;
    const id = this.getNewInternalId();
    this.toastStack$.next([{
      id,
      status: "pending",
      onElementReady,
      entryType: "interactive",
      themeKey: undefined // not used with interactive toasts. This exists purely to make the type system happy.
    }, ...this.toastStack$.getValue()]);
    return {
      /**
       * This function can be called multiple times, so we need to make sure we use the
       * entered status if that's already present, otherwise entering to fade the toast in.
       */
      queue: () => {
        const {
          entry,
          index
        } = this.getEntry(id);
        if (!entry) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("SdsToastStateService: tried to queue a toast that was not found in" + " the stack. This is probably a bug. Did you accidentally call" + " cancelRequest already?");
          }
          return;
        }
        if (entry.status === "entered" || entry.status === "entering") {
          // Don't do anything if it's already open or opening.
          return;
        }
        const stack = this.toastStack$.getValue();
        const updatedElement = {
          ...stack[index],
          status: "entering"
        };
        this.toastStack$.next([
        // In case it's not already on top, move it to the front so it renders first.
        updatedElement, ...stack.slice(0, index), ...stack.slice(index + 1)]);

        // Start a timer to remove it after the exit animation.
        this.startTimeout(id, motion.timing.default, () => {
          // update the status to entered.
          this.toastStack$.next(this.toastStack$.getValue().map(toast => {
            if (toast.id === id) {
              return {
                ...toast,
                status: "entered"
              };
            } else {
              return toast;
            }
          }));
          this.queueForRemoval(id, autoCloseDelay);
        });
      },
      /**
       * Starts the removal animation for the toast. After it's completed, sets the toast's
       * status back to "pending".
       *
       * To fully remove the toast from the service, please call cancelRequest().
       */
      remove: () => {
        const {
          entry
        } = this.getEntry(id);
        if (!entry) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("SdsToastStateService: tried to remove a toast that was not found in" + " the stack. This is probably a bug. Did you accidentally call" + " cancelRequest already?");
          }
          return;
        }
        if (entry.status === "pending" || entry.status === "exiting") {
          // Don't do anything if it's already in the process of exiting or hasn't entered
          // yet.
          return;
        }
        this.queueForRemoval(id, 0);
      },
      /**
       * Fully removes the toast from the stack.
       *
       * Calling queue after this will result in a no-op. Only call this when you know you are
       * fully done with this toast. If you need another toast, please call requestToast
       * again.
       */
      cancelRequest: () => {
        this.queueForRemoval(id, 0);
      }
    };
  };

  /**
   * Called by the code that wants to be responsible for rendering toasts. The provided callbacks
   * will be called with the contents of the current toast stack.
   *
   * Returns an object with functions to control the stack.
   */
  registerToaster = toasterCallbacks => {
    const sub = this.toastStack$.subscribe(toasterCallbacks.setToastQueue);
    return {
      pauseAutoRemove: this.pauseAutoRemove,
      restartAutoRemove: autoCloseDelay => {
        this.restartAutoRemove(autoCloseDelay);
      },
      /**
       * Call to remove a toast from the stack. This should be called when the user clicks
       * the close icon on a toast.
       */
      removeToast: toastId => {
        this.queueForRemoval(toastId, 0);
      },
      /**
       * Call to stop receiving updates from the toast stack. This should be called when the
       * component that is rendering the toasts is unmounted.
       */
      unregister: () => {
        sub.unsubscribe();
      }
    };
  };

  /**
   * Call to restart the auto-remove timers for any toasts currently in the "entered"
   * state. This should be called when the user stops interacting with the toast stack.
   */
  restartAutoRemove = (() => {
    var _this = this;
    return function () {
      let autoCloseDelay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : AUTO_CLOSE_DELAY_MS;
      for (const toast of _this.toastStack$.getValue()) {
        if (toast.status === "entered") {
          const existingTimer = _this.timerMap.get(toast.id);
          window.clearTimeout(existingTimer);
          _this.queueForRemoval(toast.id, autoCloseDelay);
        }
      }
    };
  })();

  /**
   * Call to pause the auto-remove timers for any toasts currently in the "entered"
   * state. This should be called when the user starts interacting with the toast stack.
   */
  pauseAutoRemove = () => {
    for (const toast of this.toastStack$.getValue()) {
      const existingTimer = this.timerMap.get(toast.id);
      window.clearTimeout(existingTimer);
    }
    this.toastStack$.next(this.toastStack$.getValue().map(prev => ({
      ...prev,
      // If the status is pending that means the user hasn't requested it to show
      // yet so don't show it now. If not, set the status to "entered" so it will
      // show up on the screen and stay there.
      status: prev.status === "pending" ? "pending" : "entered"
    })));
  };
  getEntry(id) {
    const foundIndex = this.toastStack$.getValue().findIndex(toast => toast.id === id);
    if (foundIndex === -1) {
      /* istanbul ignore next */
      throw new Error(`SdsToastStateService: tried to get a toast that was not found in the stack. This is probably a bug.`);
    }
    return {
      index: foundIndex,
      entry: this.toastStack$.getValue()[foundIndex]
    };
  }
  getNewInternalId = (() => {
    let id = 1;
    return () => `id_${id++}`;
  })();
  queueForRemoval(id, timeout) {
    // If it's already queued for removal, don't do anything. This helps w/ double-clicks on the
    // close icon. Otherwise, one click sets it to 'exiting', and the next tries to do it again,
    // but clears the timer that actually does the removal, then short-circuits the function.
    const {
      entry
    } = this.getEntry(id);
    if (entry?.status === "exiting" || entry?.status === "pending") {
      return;
    }

    // Start a timer to set the status to exiting.
    this.startTimeout(id, timeout, () => {
      const {
        entry,
        index
      } = this.getEntry(id);
      /* istanbul ignore next */
      if (!entry || entry.status === "exiting") {
        return;
      }
      const stack = this.toastStack$.getValue();
      this.toastStack$.next([...stack.slice(0, index), {
        ...entry,
        status: "exiting"
      }, ...stack.slice(index + 1)]);
      this.startTimeout(id, motion.timing.default, () => {
        const {
          entry,
          index
        } = this.getEntry(id);
        if (!entry) {
          return;
        }
        // If it's a simple toast, Start a timer to remove it after the exit animation. We don't
        // want to fully remove the interactive toast until the code using it is fully done with
        // it.
        if (entry.entryType === "simple") {
          this.toastStack$.next(this.toastStack$.getValue().filter(toast => toast.id !== id));
        } else {
          const stack = this.toastStack$.getValue();
          this.toastStack$.next([...stack.slice(0, index), {
            ...entry,
            status: "pending"
          }, ...stack.slice(index + 1)]);
        }
      });
    });
  }
  startTimeout(id, timeout, action) {
    const existingTimer = this.timerMap.get(id);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }
    const newTimeoutId = window.setTimeout(() => {
      this.timerMap.delete(id);
      action();
    }, timeout);
    this.timerMap.set(id, newTimeoutId);
  }
}
//# sourceMappingURL=SdsToastManager.js.map